import type { ChangeSummary, Cursor, EventDto, Ref } from "@nabla/api";
import { storeOf, type DurableEvent, type Store } from "./cursor.ts";

export interface Interest {
  readonly objects: readonly string[] | "todos";
  readonly stores: readonly Store[];
  readonly schedule?: true;
}

const everything = (stores: readonly Store[]): Interest => ({ objects: "todos", stores });

export function interestOf(key: string): Interest {
  const [path = "", query = ""] = key.split("?");
  const parts = path.split("/").slice(2).map(decodeURIComponent);
  const [head, id, tail] = parts;
  if (head === "visao" && id === "empresa") return everything(["r", "c", "a"]);
  if (head === "visao" && id === "conta" && tail !== undefined) return { objects: [`conta:${tail}`], stores: ["r", "c", "a"] };
  if (head === "visao" && (id === "campanha" || id === "conjunto" || id === "anuncio") && tail !== undefined) return { objects: [`${id}:${tail}`], stores: ["r", "c", "a"] };
  if (head === "mudancas" && id !== undefined) return { objects: [`mudanca:${id}`], stores: ["r", "c", "a", "p"] };
  if (head === "registro") return { ...everything(["r", "a", "p"]), ...(id === undefined ? { schedule: true } : {}) };
  if (head === "regras") return everything(["r", "p"]);
  if (head === "mudancas") return everything(["r"]);
  if (head === "contas") return { ...everything(["r", "a"]), schedule: true };
  if (head === "indice") return { objects: [], stores: [] };
  if (head === "pessoas" || head === "sessao") return everything(["p"]);
  if (head === "conversas" && id !== undefined) return { objects: [`conversa:${id}`], stores: ["c", "r", "p"] };
  if (head === "conversas") {
    const anchor = new URLSearchParams(query).get("ancora");
    return anchor === null ? everything(["c"]) : { objects: [anchor], stores: ["c"] };
  }
  return everything(["r", "c", "p", "a"]);
}

const keyOf = (ref: Ref) => `${ref.place}:${ref.id}`;

export function changesOf(event: DurableEvent): ChangeSummary[] {
  if (event.type === "revisao") return [event.change];
  return event.type === "mudanca" ? [event.change, ...(event.undoes ? [event.undoes] : [])] : [];
}

const changeObjects = (change: ChangeSummary) => [`mudanca:${change.id}`, ...[change.target, change.account, ...change.path].map(keyOf)];

export function objectsOf(event: DurableEvent): string[] | "todos" {
  switch (event.type) {
    case "mudanca":
    case "revisao":
      return [...changesOf(event).flatMap(changeObjects), ...(event.type === "mudanca" ? event.also ?? [] : [])];
    case "registro":
      return journalObjects(event.item);
    case "conversa":
      return [`conversa:${event.thread.id}`, keyOf(event.thread.anchor), ...(event.nota ? [keyOf(event.nota.anchor)] : []), ...event.above.map(keyOf)];
    case "alerta":
      return [...(event.alert.object ? [keyOf(event.alert.object)] : []), ...(event.alert.account ? [keyOf(event.alert.account)] : [])];
    case "dados":
      return [...event.build.campaigns.map((id) => `campanha:${id}`), ...event.build.accounts.map((id) => `conta:${id}`)];
    case "modulo":
      return event.objects === "todos" ? "todos" : [...event.objects];
    case "agenda":
    case "atualizacao":
    case "pessoas":
      return [];
  }
}

function journalObjects(item: Extract<EventDto, { type: "registro" }>["item"]): string[] | "todos" {
  if (item.type === "regra-escrita" && item.object === undefined) return "todos";
  const own = [...(item.object ? [keyOf(item.object)] : []), ...(item.path ?? []).map(keyOf)];
  if (item.type === "observacao") return [...own, keyOf(item.observation.object)];
  if (item.type === "regra") return [...own, ...[item.setting.account, item.setting.campaign].flatMap((ref) => (ref ? [keyOf(ref)] : []))];
  if ("change" in item) return [...own, `mudanca:${item.change.id}`];
  return own;
}

export function touches(interest: Interest, stores: readonly Store[], objects: readonly string[] | "todos"): boolean {
  if (!interest.stores.some((store) => stores.includes(store))) return false;
  return interest.objects === "todos" || objects === "todos" || interest.objects.some((object) => objects.includes(object));
}

export function outdatedBy(interest: Interest, cursor: Cursor, event: DurableEvent, objects: readonly string[] | "todos"): boolean {
  const store = storeOf(event);
  if (cursor[store] >= event.cursor[store]) return false;
  return event.type === "agenda" ? interest.stores.includes(store) && interest.schedule === true : touches(interest, [store], objects);
}
